import { ReactNode, useState } from 'react'
import styled from 'styled-components'
import {
  Button,
  ChevronDownIcon,
  ChevronUpIcon,
  H,
  P,
  Wrapper,
} from '@farewill/ui'
import { COLOR, GTR } from '@farewill/ui/tokens'

import Table from 'components/table'
import Badge from 'components/styled/badge'
import { FuneralPlanVersion } from 'lib/models/funeral-plan'
import { formatToHuman } from 'utils/helpers'

import PaymentAgreementDetails from './payment-agreement-details'
import PaymentLink from './payment-link'
import { agreementBadge, formatPence, formatShortDay } from './payment-shared'
import { PaymentTimelineRow } from './payment-versions'

/** Date, ID, Provider, Description, Status, Amount. The date and amount
 * columns are never narrower than their content, so neither a date nor
 * "See details" wraps. */
const COLUMNS =
  'minmax(max-content, 1fr) 0.7fr 0.8fr 1.6fr 1fr minmax(max-content, 1fr)'

/** Amounts, and "See details" in their column, sit against the right edge */
const StyledAmount = styled.div`
  text-align: right;
`

/** "See details" underlined as a link, with its chevron beside it, not
 * underlined */
const StyledSeeDetails = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${GTR.XXS};
  padding: 0;
  background: none;
  border: none;
  font: inherit;
  color: ${COLOR.GREY.DARK};
  white-space: nowrap;
  cursor: pointer;

  span {
    text-decoration: underline;
  }
`

const NoAgreement = () => (
  <>
    {' '}
    <Badge $state="error">No agreement</Badge>
  </>
)

const AgreementRows = ({
  date,
  version,
}: {
  date: string
  version: FuneralPlanVersion
}): React.ReactElement => {
  const [open, setOpen] = useState(false)
  const badge = agreementBadge(version)

  return (
    <>
      <Table.Row
        gridTemplateCols={COLUMNS}
        highlight
        onClick={() => setOpen(!open)}
        data-testid="timeline-row"
      >
        <Table.Col>{formatShortDay(date)}</Table.Col>
        <Table.Col strong span={4}>
          {version.name}
          {badge && (
            <>
              {' '}
              <Badge $state={badge.state}>{badge.label}</Badge>
            </>
          )}
        </Table.Col>
        <Table.Col>
          <StyledAmount>
            {/* the row's click opens it; the button is there for keyboards */}
            <StyledSeeDetails type="button" aria-expanded={open}>
              <span>{open ? 'Hide details' : 'See details'}</span>
              {open ? (
                <ChevronUpIcon size="S" inline />
              ) : (
                <ChevronDownIcon size="S" inline />
              )}
            </StyledSeeDetails>
          </StyledAmount>
        </Table.Col>
      </Table.Row>
      {open && (
        <Table.Row gridTemplateCols="1fr" highlight>
          <PaymentAgreementDetails version={version} />
        </Table.Row>
      )}
    </>
  )
}

type Props = {
  /** Every payment, refund and agreement, newest first */
  rows: PaymentTimelineRow[]
  /** Buttons for the timeline, such as opening the customer on Stripe */
  actions?: ReactNode
  /** Offered for Square virtual terminal payments, whose refunds are not
   * recorded automatically */
  onEditPayment?: (invoiceId: number) => void
}

/**
 * Every payment, refund and agreement on the plan, newest first (2.14). A
 * refund has its own row naming the payment it refunds, and anything not
 * linked to an agreement is flagged.
 */
const PaymentTimeline = ({
  rows,
  actions,
  onEditPayment,
}: Props): React.ReactElement => (
  <Wrapper data-testid="payment-timeline">
    <H tag="h3" size="S">
      Payment timeline
    </H>
    {actions && <Wrapper margin={[0, 0, 'S']}>{actions}</Wrapper>}
    <Table withHeaders padded>
      <Table.Row gridTemplateCols={COLUMNS}>
        <Table.Col strong>Date</Table.Col>
        <Table.Col strong>ID</Table.Col>
        <Table.Col strong>Provider</Table.Col>
        <Table.Col strong>Description</Table.Col>
        <Table.Col strong>Status</Table.Col>
        <Table.Col strong>
          <StyledAmount>Amount</StyledAmount>
        </Table.Col>
      </Table.Row>

      {rows.map((entry) => {
        if (entry.kind === 'agreement') {
          return (
            <AgreementRows
              key={`agreement-${entry.version.agreement.id}`}
              date={entry.date}
              version={entry.version}
            />
          )
        }

        const provider = entry.provider ? formatToHuman(entry.provider) : ''

        if (entry.kind === 'refund') {
          return (
            <Table.Row
              key={`refund-${entry.id}`}
              gridTemplateCols={COLUMNS}
              data-testid="timeline-row"
            >
              <Table.Col>{formatShortDay(entry.date)}</Table.Col>
              <Table.Col>
                {entry.refundsPaymentId}-R{entry.number}
              </Table.Col>
              <Table.Col>{provider}</Table.Col>
              <Table.Col>Refund of {entry.refundsPaymentId}</Table.Col>
              <Table.Col>
                {entry.status}
                {!entry.hasAgreement && <NoAgreement />}
              </Table.Col>
              <Table.Col>
                <StyledAmount>{formatPence(entry.amountInPence)}</StyledAmount>
              </Table.Col>
            </Table.Row>
          )
        }

        const canEdit =
          onEditPayment &&
          entry.provider === 'square' &&
          !entry.paymentProviderInvoiceId

        return (
          <Table.Row
            key={`payment-${entry.id}`}
            gridTemplateCols={COLUMNS}
            data-testid="timeline-row"
          >
            <Table.Col>{formatShortDay(entry.date)}</Table.Col>
            <Table.Col>{entry.id}</Table.Col>
            <Table.Col>{provider}</Table.Col>
            <Table.Col>
              {entry.provider && (
                <PaymentLink
                  paymentProvider={entry.provider}
                  reference={entry.reference ?? undefined}
                  paymentProviderInvoiceId={
                    entry.paymentProviderInvoiceId ?? undefined
                  }
                />
              )}
            </Table.Col>
            <Table.Col>
              {entry.status}
              {!entry.hasAgreement && <NoAgreement />}
            </Table.Col>
            <Table.Col>
              <StyledAmount>
                {formatPence(entry.amountInPence)}
                {canEdit && (
                  <>
                    {' '}
                    <Button onClick={() => onEditPayment(entry.id)}>
                      edit
                    </Button>
                  </>
                )}
              </StyledAmount>
            </Table.Col>
          </Table.Row>
        )
      })}
    </Table>
    {rows.length === 0 && <P>No payments yet.</P>}
  </Wrapper>
)

export default PaymentTimeline
